import { useQuery } from "@tanstack/react-query";
import { useApi } from "@utils/useApi";
import { EcommerceShoppingCartClient, ShoppingCartLineDto } from "@lib/ShiOneClient";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../auth/authorizationTypes";
import { SHOPPING_CART_LINES_QUERY_KEY } from "@features/unified-user-header-feature/utils/HeaderQueryKeys";
import { cartKeys } from "./cartQueryKeys";

/** Query key for the enriched cart lines (device + warranty children) used by the full cart page. */
export { SHOPPING_CART_LINES_QUERY_KEY };

/**
 * Fetches cart lines enriched with their attached warranty/support children (SPU-533's
 * `getShoppingCartLines`) so the full cart page can render devices with indented warranty
 * rows. Separate from the mini-cart's flat `useGetShoppingCart` because it carries the
 * parent/child structure.
 */
export interface UseGetShoppingCartLinesOptions {
  // Checkout opts in so a cart changed in another tab is picked up on return; elsewhere it stays off.
  refetchOnWindowFocus?: boolean | "always";
}

export default function useGetShoppingCartLines(
  enabled = true,
  { refetchOnWindowFocus = false }: UseGetShoppingCartLinesOptions = {},
) {
  const api = useApi(EcommerceShoppingCartClient);
  const { hasPermission } = useClaims();
  const canViewShoppingCart = hasPermission(ShiOnePermission.shoppingCartView);

  return useQuery<ShoppingCartLineDto[]>({
    queryKey: cartKeys.lines(),
    queryFn: () => api.getShoppingCartLines(),
    enabled: canViewShoppingCart && enabled,
    refetchOnWindowFocus,
  });
}
